import { Button, IconButton, Tag } from '@ads/components-react';

import { ChevronRightIcon, EyeIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetApiV1ExamCorrectionDashboardClassroomdisciplineid } from '@/shared/api/generated';
import type {
  CorrectionDashboardDetailItemRepositoryResponse,
  CorrectionDashboardItemRepositoryResponse,
} from '@/shared/api/generated/types';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Skeleton,
} from '@/shared/ui/atoms';
import { EmptyState } from '@/shared/ui/molecules/EmptyState';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(isoDate: string | undefined | null): string {
  if (!isoDate) return '—';
  return new Date(isoDate).toLocaleDateString('pt-BR');
}

// Mobile: chevron + nome + pendências | Desktop: + datas
const COLS = 'grid-cols-[2.5rem_1fr_auto] md:grid-cols-[2.5rem_1fr_11rem_11rem_7.5rem]';
// Sub-tabela de avaliações (dentro do painel expandido, sem coluna de chevron)
const SUB_COLS = 'grid-cols-[1fr_auto] md:grid-cols-[1fr_10rem_10rem_6rem_2.5rem]';

// ─── DashboardSkeleton ────────────────────────────────────────────────────────

function DashboardSkeleton() {
  return (
    <div className="overflow-hidden rounded-sm border-hairline border-ctx-layout-border p-xxxs">
      {/* Header skeleton */}
      <div className={`grid ${COLS} gap-sm px-mini py-micro`}>
        <div />
        <Skeleton className="w-24 h-xxs" />
        <Skeleton className="w-xl hidden h-xxs md:block" />
        <Skeleton className="w-xl hidden h-xxs md:block" />
        <Skeleton className="w-sm h-xxs" />
      </div>

      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className={`grid ${COLS} items-center gap-sm border-t-hairline border-ctx-layout-border px-mini py-xxs`}
        >
          <Skeleton shape="circle" className="size-mini" />
          <Skeleton className="h-mini w-3/4" />
          <Skeleton className="w-xl hidden h-mini md:block" />
          <Skeleton className="w-xl hidden h-mini md:block" />
          <Skeleton className="w-sm h-sm rounded-pill" />
        </div>
      ))}
    </div>
  );
}

// ─── DisciplineContent ────────────────────────────────────────────────────────

interface DisciplineContentProps {
  readonly disciplineId: number;
  readonly enabled: boolean;
}

function DisciplineContent({ disciplineId, enabled }: DisciplineContentProps) {
  const { t } = useTranslation('examCorrection');
  const navigate = useNavigate();

  // `isPending` (não `isLoading`) — `isLoading` do TanStack Query é `isPending &&
  // isFetching`; ao alternar `enabled` (abrir o accordion da disciplina) há uma
  // janela em que a busca ainda não começou mas já não há dado, e `isLoading`
  // reportaria `false` nela, piscando o empty state antes do conteúdo real.
  const { data, isPending: isLoading } = useGetApiV1ExamCorrectionDashboardClassroomdisciplineid(
    disciplineId,
    undefined,
    { query: { enabled } },
  );

  const exams: CorrectionDashboardDetailItemRepositoryResponse[] = data?.data ?? [];

  if (isLoading) {
    return (
      <div className="flex flex-col gap-nano bg-ctx-layout-body p-xxxs">
        {/* Sub-header skeleton */}
        <div className={`grid ${SUB_COLS} gap-sm px-mini py-nano`}>
          <Skeleton className="h-xxs w-xl" />
          <Skeleton className="hidden h-xxs w-lg md:block" />
          <Skeleton className="hidden h-xxs w-lg md:block" />
          <Skeleton className="hidden h-xxs w-sm md:block" />
          <div />
        </div>
        {Array.from({ length: 2 }).map((_, i) => (
          <div
            key={i}
            className={`grid ${SUB_COLS} h-[4.5rem] items-center gap-sm rounded-md border-hairline border-ctx-layout-border px-xxs py-micro`}
          >
            <Skeleton className="h-mini w-2/3" />
            <Skeleton className="hidden h-mini w-lg md:block" />
            <Skeleton className="hidden h-mini w-lg md:block" />
            <Skeleton className="hidden h-sm w-sm rounded-pill md:block" />
            <Skeleton shape="circle" className="size-md" />
          </div>
        ))}
      </div>
    );
  }

  if (exams.length === 0) {
    return (
      <div className="bg-ctx-layout-body p-xxxs">
        <EmptyState
          compact
          title={t('dashboard.noExams')}
          description={t('dashboard.noExamsDescription')}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-nano bg-ctx-layout-body p-xxxs">
      {/* Sub-header */}
      <div className={`grid ${SUB_COLS} gap-sm px-mini py-nano`}>
        <span className="text-ctx-content-title ts-subtitle-xxs">
          {t('dashboard.table.columns.exam')}
        </span>
        <span className="hidden text-ctx-content-title ts-subtitle-xxs md:block">
          {t('dashboard.table.columns.startDate')}
        </span>
        <span className="hidden text-ctx-content-title ts-subtitle-xxs md:block">
          {t('dashboard.table.columns.endDate')}
        </span>
        <span className="hidden text-ctx-content-title ts-subtitle-xxs md:block">
          {t('dashboard.table.columns.pending')}
        </span>
        <div />
      </div>

      {/* Exam cards */}
      {exams.map(exam => {
        const hasPending = (exam.correctionPendingCount ?? 0) > 0;

        return (
          <div
            key={exam.id}
            className={`grid ${SUB_COLS} h-[4.5rem] items-center gap-sm rounded-md border-hairline border-ctx-layout-border px-xxs py-micro transition-colors`}
          >
            <span className="truncate text-ctx-content-base ts-paragraph-xxs">{exam.examName}</span>

            <span className="hidden text-ctx-content-placeholder ts-paragraph-xxs md:block">
              {formatDate(exam.startDate)}
            </span>

            <span className="hidden text-ctx-content-placeholder ts-paragraph-xxs md:block">
              {formatDate(exam.endDate)}
            </span>

            <div className="hidden items-center md:flex">
              {hasPending ? (
                <Tag
                  status="helper"
                  label={String(exam.correctionPendingCount)}
                  custom={{ text: 'text-nowrap' }}
                />
              ) : (
                <Tag
                  status="positive"
                  label={t('dashboard.noPending')}
                  custom={{ text: 'text-nowrap' }}
                />
              )}
            </div>

            <div className="flex items-center justify-end">
              <IconButton
                size="md"
                hierarchy="tertiary"
                icon={hasPending ? PencilSquareIcon : EyeIcon}
                type="button"
                onClick={() =>
                  void navigate({
                    to: '/exam/correction/$examPublishId',
                    params: { examPublishId: String(exam.id ?? 0) },
                  })
                }
                ariaLabel={
                  hasPending ? t('dashboard.actions.correct') : t('dashboard.actions.view')
                }
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── DashboardTable ───────────────────────────────────────────────────────────

export interface DashboardTableProps {
  readonly items: CorrectionDashboardItemRepositoryResponse[];
  readonly isLoading: boolean;
  readonly currentPage: number;
  readonly totalPages: number;
  readonly totalCount: number;
  readonly onPageChange: (page: number) => void;
}

/**
 * Organism DashboardTable — tabela de disciplinas com accordion de provas.
 *
 * Cada linha representa uma disciplina. Ao expandir, carrega as provas
 * dessa disciplina com seus status de correção pendente.
 */
export function DashboardTable({
  items,
  isLoading,
  currentPage,
  totalPages,
  totalCount,
  onPageChange,
}: DashboardTableProps) {
  const { t } = useTranslation('examCorrection');

  const [openDisciplineIds, setOpenDisciplineIds] = useState<Set<number>>(new Set());

  function handleValueChange(openValues: string[]) {
    setOpenDisciplineIds(new Set(openValues.map(Number)));
  }

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  if (items.length === 0) {
    return (
      <div className="rounded-sm border-hairline border-ctx-layout-border p-xxxs">
        <EmptyState title={t('dashboard.empty')} description={t('dashboard.emptyDescription')} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-sm">
      <div className="overflow-hidden rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxxs">
        {/* Header principal */}
        <div className={`grid ${COLS} gap-sm px-mini py-micro`}>
          <div />
          <span className="text-ctx-content-title ts-subtitle-xxs">
            {t('dashboard.table.columns.discipline')}
          </span>
          <span className="hidden text-ctx-content-title ts-subtitle-xxs md:block">
            {t('dashboard.table.columns.startDate')}
          </span>
          <span className="hidden text-ctx-content-title ts-subtitle-xxs md:block">
            {t('dashboard.table.columns.endDate')}
          </span>
          <span className="text-ctx-content-title ts-subtitle-xxs">
            {t('dashboard.table.columns.pending')}
          </span>
        </div>

        <Accordion type="multiple" onValueChange={handleValueChange}>
          {items.map(item => {
            const disciplineId = item.id ?? 0;
            const isOpen = openDisciplineIds.has(disciplineId);
            const hasPending = (item.correctionPendingCount ?? 0) > 0;
            const dateValue = item.isOnDemand
              ? t('dashboard.onDemand')
              : formatDate(item.startDate);
            const dateEndValue = item.isOnDemand
              ? t('dashboard.onDemand')
              : formatDate(item.endDate);

            return (
              <AccordionItem
                key={disciplineId}
                value={String(disciplineId)}
                className="border-t-hairline border-ctx-layout-border"
              >
                <AccordionTrigger
                  className={[
                    `grid ${COLS} h-[4.5rem] content-center items-center gap-sm px-mini transition-colors`,
                    isOpen
                      ? 'bg-ctx-interactive-secondary border-b-hairline border-ctx-layout-border hover:bg-ctx-interactive-secondaryHover'
                      : 'hover:bg-ctx-layout-body',
                  ].join(' ')}
                >
                  <IconButton
                    ariaLabel=""
                    hierarchy="secondary"
                    size="sm"
                    icon={ChevronRightIcon}
                    className="group-data-[state=open]:rotate-90"
                  />

                  <span className="ts-paragraph-xxs text-ctx-content-base truncate">
                    {item.disciplineName}
                  </span>

                  <span className="hidden ts-paragraph-xxs text-ctx-content-base md:block">
                    {dateValue}
                  </span>

                  <span className="hidden ts-paragraph-xxs text-ctx-content-base md:block">
                    {dateEndValue}
                  </span>

                  <div className="flex items-center justify-end">
                    {hasPending ? (
                      <Tag
                        status="helper"
                        label={String(item.correctionPendingCount)}
                        custom={{ text: 'text-nowrap' }}
                      />
                    ) : (
                      <Tag
                        status="positive"
                        label={t('dashboard.noPending')}
                        custom={{ text: 'text-nowrap' }}
                      />
                    )}
                  </div>
                </AccordionTrigger>

                <AccordionContent className="bg-ctx-layout-surface">
                  <DisciplineContent
                    disciplineId={disciplineId}
                    enabled={openDisciplineIds.has(disciplineId)}
                  />
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex flex-col gap-xs sm:flex-row sm:items-center sm:justify-between">
          <span className="text-ctx-content-placeholder ts-caption-xxs">
            {t('dashboard.pagination.showing', {
              count: items.length,
              total: totalCount,
            })}
          </span>

          <div className="flex items-center gap-nano">
            <Button
              hierarchy="secondary"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage <= 1}
            >
              {t('dashboard.pagination.previous')}
            </Button>

            <span className="text-ctx-content-base ts-caption-xxs">
              {currentPage} / {totalPages}
            </span>

            <Button
              hierarchy="secondary"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
            >
              {t('dashboard.pagination.next')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
